/**
 * storageAccountApi.ts
 * Feature: 116 - Entity Detail Modals
 * Author: Peter Dobler
 * Date: 2026-01-29
 * Constitution Version: 1.12.0
 *
 * Purpose: Frontend API service for storage account entities.
 */

import { apiClient } from './apiClient';

/** Storage Account entity */
export interface StorageAccount {
  id: string;
  name: string;
  resource_id: string;
  subscription_id: string;
  resource_group: string;
  location: string;
  environment_id: string;
  workspace_id?: string;
  status: 'active' | 'inactive' | 'archived';
  sku_name?: string;
  sku_tier?: string;
  access_tier?: string;
  kind?: string;
  is_hns_enabled?: boolean;
  capacity_gb?: number;
  used_capacity_gb?: number;
  used_percent?: number;
  blob_container_count?: number;
  primary_endpoint?: string;
  created_date?: string;
  last_modified_date?: string;
  // Backup configuration
  endpoint_url?: string;
  container_name?: string;
  storage_purpose?: string;
  // Control Plane metadata
  display_name?: string;
  owner_id?: string;
  notes?: string;
  tags?: string[];
  // Computed fields
  owner_name?: string;
  environment_name?: string;
  workspace_name?: string;
  created_at: string;
  updated_at: string;
}

/** Storage Account update request */
export interface UpdateStorageAccountRequest {
  display_name?: string;
  owner_id?: string;
  notes?: string;
  tags?: string[];
  // Backup configuration
  endpoint_url?: string;
  container_name?: string;
  storage_purpose?: string;
}

/** Storage Account filters */
export interface StorageAccountFilters {
  environment_id?: string;
  workspace_id?: string;
  subscription_id?: string;
  resource_group?: string;
  status?: 'active' | 'inactive' | 'archived';
  sku_tier?: string;
  access_tier?: string;
  is_hns_enabled?: boolean;
  min_capacity_gb?: number;
  max_used_percent?: number;
}

/**
 * Get storage account by ID
 */
export async function getStorageAccountById(id: string): Promise<StorageAccount> {
  const response = await apiClient.get<{ data: StorageAccount }>(`/api/v1/storage-accounts/${id}`);
  return response.data.data;
}

/**
 * Get all storage accounts with optional filters
 */
export async function getStorageAccounts(filters?: StorageAccountFilters): Promise<StorageAccount[]> {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    });
  }
  const queryString = params.toString();
  const url = queryString ? `/api/v1/storage-accounts?${queryString}` : '/api/v1/storage-accounts';
  const response = await apiClient.get<{ data: StorageAccount[] }>(url);
  return response.data.data;
}

/**
 * Update storage account (Control Plane metadata only)
 */
export async function updateStorageAccount(
  id: string,
  data: UpdateStorageAccountRequest
): Promise<StorageAccount> {
  const response = await apiClient.patch<{ data: StorageAccount }>(`/api/v1/storage-accounts/${id}`, data);
  return response.data.data;
}

/**
 * Get storage account counts by environment (for dashboard)
 */
export async function getStorageAccountCountsByEnvironment(): Promise<
  Array<{ environment_id: string; count: number }>
> {
  const response = await apiClient.get<{ data: Array<{ environment_id: string; count: number }> }>(
    '/api/v1/storage-accounts/counts-by-environment'
  );
  return response.data.data;
}

// TanStack Query hooks

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

/** Query key for storage accounts */
export const STORAGE_ACCOUNT_QUERY_KEYS = {
  all: ['storage-accounts'] as const,
  lists: () => [...STORAGE_ACCOUNT_QUERY_KEYS.all, 'list'] as const,
  list: (filters?: StorageAccountFilters) => [...STORAGE_ACCOUNT_QUERY_KEYS.lists(), filters] as const,
  details: () => [...STORAGE_ACCOUNT_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...STORAGE_ACCOUNT_QUERY_KEYS.details(), id] as const,
};

/**
 * Hook to fetch storage account by ID
 */
export function useStorageAccount(
  id: string | null,
  options?: Omit<UseQueryOptions<StorageAccount>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: STORAGE_ACCOUNT_QUERY_KEYS.detail(id ?? ''),
    queryFn: () => getStorageAccountById(id!),
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to fetch all storage accounts
 */
export function useStorageAccounts(
  filters?: StorageAccountFilters,
  options?: Omit<UseQueryOptions<StorageAccount[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: STORAGE_ACCOUNT_QUERY_KEYS.list(filters),
    queryFn: () => getStorageAccounts(filters),
    ...options,
  });
}

/**
 * Hook to update storage account
 */
export function useUpdateStorageAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStorageAccountRequest }) =>
      updateStorageAccount(id, data),
    onSuccess: (updatedAccount) => {
      // Invalidate and update cache
      queryClient.invalidateQueries({ queryKey: STORAGE_ACCOUNT_QUERY_KEYS.all });
      queryClient.setQueryData(
        STORAGE_ACCOUNT_QUERY_KEYS.detail(updatedAccount.id),
        updatedAccount
      );
    },
  });
}

export default {
  getStorageAccountById,
  getStorageAccounts,
  updateStorageAccount,
  getStorageAccountCountsByEnvironment,
};
